import { useEffect, useState } from 'react'
import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Loader2, AlertCircle, Camera, Mail, Check, PauseCircle } from 'lucide-react'
import { Landing } from './routes/Landing'
import { Gallery } from './routes/Gallery'
import { Photo } from './routes/Photo'
import { Review } from './routes/Review'
import { Done } from './routes/Done'
import { InviteHandoff } from './routes/InviteHandoff'
import { StudioPortal } from './routes/StudioPortal'
import { PasscodeScreen } from './components/PasscodeScreen'
import { AccessGate } from './components/AccessGate'
import { readShareTokenFromUrl, readSneakTokenFromUrl, useManifest } from './state/manifest'
import { useSession } from './state/session'
import {
  fetchSneakPeek,
  requestRefresh,
  type ExpiredContact,
  type SneakPeekManifest,
} from './api/client'

export default function App() {
  const manifest = useManifest((s) => s.manifest)
  const location = useLocation()
  const navigate = useNavigate()
  const activeShareToken = useManifest((s) => s.shareToken)
  // HashRouter normally exposes `#/invite/<token>` as pathname, but retain
  // a direct hash fallback so the emitted HTTPS URL keeps working if the
  // router has not mounted yet or a host wraps the app differently.
  const inviteToken =
    inviteTokenFromPath(location.pathname) ??
    (typeof window === 'undefined' ? null : inviteTokenFromHashRoute(window.location.hash))
  // Keep the active gallery capability in the hash when route navigation
  // moves from the landing URL to a photo/review. A reload must reopen this
  // gallery rather than unexpectedly dropping the client into studio sign-in.
  useEffect(() => {
    if (inviteToken || !manifest || !activeShareToken || readShareTokenFromUrl()) return
    const query = new URLSearchParams(location.search)
    query.set('t', activeShareToken)
    navigate({ pathname: location.pathname, search: `?${query}` }, { replace: true })
  }, [inviteToken, manifest, activeShareToken, location.pathname, location.search, navigate])
  const isStudioMode = !inviteToken && !readShareTokenFromUrl() && !manifest
  return (
    <div className={`stage ${isStudioMode ? 'stage--studio' : ''}`}>
      <div className={`app ${isStudioMode ? 'app--studio' : ''}`}>
        <ManifestBoundary inviteToken={inviteToken} />
      </div>
    </div>
  )
}

/** Only strict, server-shaped invite tokens may enter the native handoff. */
export function inviteTokenFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/invite\/([A-Za-z0-9_-]{1,256})\/?$/)
  return match ? match[1] : null
}

/** Parse the actual `#/invite/<token>` URL emitted by shareLinkFor. */
export function inviteTokenFromHashRoute(hash: string): string | null {
  return inviteTokenFromPath(hash.startsWith('#') ? hash.slice(1) : hash)
}

function ManifestBoundary({ inviteToken }: { inviteToken: string | null }) {
  const status = useManifest((s) => s.status)
  const error = useManifest((s) => s.error)
  const manifest = useManifest((s) => s.manifest)
  const wrongPin = useManifest((s) => s.wrongPin)
  const expiredContact = useManifest((s) => s.expiredContact)
  const shareToken = useManifest((s) => s.shareToken)
  const load = useManifest((s) => s.load)
  const submitPin = useManifest((s) => s.submitPin)
  const navigate = useNavigate()
  const location = useLocation()
  const setSessionToken = useSession((s) => s.setShareToken)
  // Local in-flight flag so the passcode screen keeps its spinner while the
  // pin re-load runs (the manifest status flips to 'loading' mid-check).
  const [pinSubmitting, setPinSubmitting] = useState(false)

  useEffect(() => {
    const token = readShareTokenFromUrl()
    if (
      token &&
      (useManifest.getState().shareToken !== token || useManifest.getState().status === 'idle')
    ) {
      void load(token)
      setSessionToken(token)
    }
  }, [load, location.key, setSessionToken])

  if (inviteToken) {
    return <InviteHandoff token={inviteToken} />
  }

  // A sneak-peek slug is its own tiny surface — view-only, no voter, no
  // picks. Route it before the studio portal: this URL is what the studio
  // texts the couple hours after the shoot, and it used to land on sign-in.
  const sneakToken = readSneakTokenFromUrl()
  if (!readShareTokenFromUrl() && sneakToken) {
    return <SneakPeekScreen token={sneakToken} />
  }

  if (!readShareTokenFromUrl() && !manifest) {
    return <StudioPortal />
  }

  // Access gate: the studio restricted this link to the people it invited, so
  // holding the URL is not enough. A forwarded copy reaches this screen.
  if (status === 'access' && shareToken) {
    return <AccessGate shareToken={shareToken} />
  }

  // Passcode gate: show while the link is locked, or while a pin attempt is in
  // flight (the in-flight 'loading' status would otherwise drop us to the
  // generic loading screen and lose the input + error).
  if (status === 'passcode' || (pinSubmitting && status !== 'ready')) {
    return (
      <PasscodeScreen
        wrongPin={wrongPin}
        submitting={pinSubmitting}
        onSubmit={(pin) => {
          setPinSubmitting(true)
          void submitPin(pin).finally(() => setPinSubmitting(false))
        }}
      />
    )
  }

  if (status === 'idle' || status === 'loading') {
    return <LoadingScreen />
  }
  if (status === 'error' && !manifest && error?.includes('not_ready')) {
    // The worker answers 423 while the studio's upload has not finalised.
    // That is "come back shortly", never "broken" — give it its own calm
    // screen and let it self-heal instead of the generic failure page.
    return (
      <PreparingScreen
        onRetry={() => {
          if (shareToken) void load(shareToken)
        }}
      />
    )
  }
  if (status === 'error' && !manifest && error?.includes('paused')) {
    // The studio paused the share — a deliberate, reversible hold, nothing
    // like expiry. Say so, and re-ask on a cadence so the moment the studio
    // resumes, the gallery comes back without the viewer doing anything.
    return (
      <PausedScreen
        onRetry={() => {
          if (shareToken) void load(shareToken)
        }}
      />
    )
  }
  if (status === 'error' && !manifest) {
    return (
      <ErrorScreen
        message={error}
        contact={expiredContact}
        shareToken={shareToken}
        onRetry={() => navigate(0)}
      />
    )
  }
  if (!manifest) {
    return <NoLinkScreen />
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/photo/:index" element={<Photo />} />
      <Route path="/review" element={<Review />} />
      <Route path="/done" element={<Done />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function LoadingScreen() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[100dvh] gap-3 px-6 text-center">
      <Loader2 size={26} className="animate-spin text-[var(--text-tertiary)]" />
      <div className="text-sm text-[var(--text-secondary)]">
        Loading your photographer's gallery…
      </div>
    </div>
  )
}

function PreparingScreen({ onRetry }: { onRetry: () => void }) {
  // Self-heal: the upload usually finishes within minutes, so re-ask on a
  // modest cadence. The manifest store ignores overlapping loads.
  useEffect(() => {
    const id = setInterval(onRetry, 30_000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <div
      data-testid="preparing-screen"
      className="flex flex-col items-center justify-center min-h-[100dvh] gap-4 px-6 text-center"
    >
      <div className="w-14 h-14 rounded-full bg-[var(--surface-secondary)] border border-[var(--border-default)] grid place-items-center text-[var(--text-secondary)]">
        <Loader2 size={24} className="animate-spin" strokeWidth={2} />
      </div>
      <div className="max-w-xs">
        <div className="text-base font-semibold text-[var(--text-primary)] mb-1">
          This gallery is still being prepared
        </div>
        <div className="text-[13px] text-[var(--text-secondary)]">
          Your photographer is still uploading photos. This page checks again on its own — or come
          back in a few minutes.
        </div>
      </div>
      <button
        onClick={onRetry}
        className="px-3.5 py-2 rounded-xl bg-[var(--surface-secondary)] border border-[var(--border-default)] text-sm text-[var(--text-primary)]"
      >
        Check now
      </button>
    </div>
  )
}

function PausedScreen({ onRetry }: { onRetry: () => void }) {
  // Same self-heal cadence as PreparingScreen: the pause is reversible, so
  // keep asking and the gallery reappears on its own once the studio resumes.
  useEffect(() => {
    const id = setInterval(onRetry, 30_000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <div
      data-testid="paused-screen"
      className="flex flex-col items-center justify-center min-h-[100dvh] gap-4 px-6 text-center"
    >
      <div className="w-14 h-14 rounded-full bg-[var(--surface-secondary)] border border-[var(--border-default)] grid place-items-center text-[var(--text-secondary)]">
        <PauseCircle size={26} strokeWidth={2} />
      </div>
      <div className="max-w-xs">
        <div className="text-base font-semibold text-[var(--text-primary)] mb-1">
          This gallery is temporarily unavailable
        </div>
        <div className="text-[13px] text-[var(--text-secondary)]">
          Your photographer has paused sharing for a moment. This page checks again on its own — no
          need for a new link.
        </div>
      </div>
      <button
        onClick={onRetry}
        className="px-3.5 py-2 rounded-xl bg-[var(--surface-secondary)] border border-[var(--border-default)] text-sm text-[var(--text-primary)]"
      >
        Check now
      </button>
    </div>
  )
}

function SneakPeekScreen({ token }: { token: string }) {
  const [data, setData] = useState<SneakPeekManifest | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    fetchSneakPeek(token)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: Error) => {
        if (alive) setError(e.message)
      })
    return () => {
      alive = false
    }
  }, [token])
  if (error) {
    return (
      <div
        data-testid="sneak-peek-error"
        className="flex flex-col items-center justify-center min-h-[100dvh] gap-3 px-6 text-center"
      >
        <AlertCircle size={26} className="text-[var(--accent-danger)]" />
        <div className="text-sm text-[var(--text-secondary)]">
          This sneak peek is no longer available. Ask your photographer for the full gallery link.
        </div>
      </div>
    )
  }
  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[100dvh] gap-3 px-6 text-center">
        <Loader2 size={26} className="animate-spin text-[var(--text-tertiary)]" />
      </div>
    )
  }
  const photos = [...data.photos].sort((a, b) => a.position - b.position)
  return (
    <div data-testid="sneak-peek-screen" className="min-h-[100dvh] px-4 py-8">
      <div className="mx-auto max-w-lg">
        <div className="mb-1 text-[11px] uppercase tracking-[0.2em] text-[var(--text-tertiary)]">
          Sneak peek · {data.photographer.name}
        </div>
        <h1 className="mb-5 text-2xl font-semibold text-[var(--text-primary)]">
          {data.event.title}
        </h1>
        <div className="grid grid-cols-1 gap-3">
          {photos.map((p) => (
            <img
              key={p.id}
              src={p.displayUrl}
              alt=""
              loading="lazy"
              className="w-full rounded-2xl object-cover"
            />
          ))}
        </div>
        <p className="mt-6 text-center text-[12px] text-[var(--text-tertiary)]">
          The full gallery is on its way — your photographer will share it soon.
        </p>
      </div>
    </div>
  )
}

function ErrorScreen({
  message,
  contact,
  shareToken,
  onRetry,
}: {
  message: string | null
  contact: ExpiredContact | null
  shareToken: string | null
  onRetry: () => void
}) {
  // 'revoked' is a no-longer-available state the worker reports on a 410
  // alongside 'expired'; both mean "ask the studio for a fresh link".
  // ('paused' is reversible and routes to PausedScreen before we get here.)
  const isGone = message?.includes('expired') || message?.includes('revoked')
  const friendly = isGone
    ? 'This gallery link is no longer active. Ask your photographer for a fresh one.'
    : message?.includes('not_found')
      ? "We couldn't find this gallery. Check the link?"
      : "We couldn't load this gallery."

  const studioName = contact?.name?.trim() || null

  return (
    <div
      data-testid="error-screen"
      className="flex flex-col items-center justify-center min-h-[100dvh] gap-4 px-6 text-center"
    >
      <div className="w-14 h-14 rounded-full bg-[var(--accent-danger-soft)] border border-[var(--accent-danger-ring)] grid place-items-center text-[var(--accent-danger)]">
        <AlertCircle size={26} strokeWidth={2} />
      </div>
      <div className="max-w-xs">
        <div className="text-base font-semibold text-[var(--text-primary)] mb-1">
          {isGone ? 'This link has expired' : 'Something went wrong'}
        </div>
        <div className="text-[13px] text-[var(--text-secondary)]">{friendly}</div>
      </div>

      {isGone && studioName && (
        <ExpiredContactCard name={studioName} contact={contact} shareToken={shareToken} />
      )}

      <button
        onClick={onRetry}
        className="px-3.5 py-2 rounded-xl bg-[var(--surface-secondary)] border border-[var(--border-default)] text-sm text-[var(--text-primary)]"
      >
        Try again
      </button>
    </div>
  )
}

/**
 * Studio contact + a "Request a refresh" button on the expired/revoked screen.
 * The contact (name/email) rides along on the worker's 410 body so a returning
 * client whose link lapsed can reach the right studio instead of hitting a dead
 * end. The button posts a refresh request the studio sees in its link tracking.
 */
function ExpiredContactCard({
  name,
  contact,
  shareToken,
}: {
  name: string
  contact: ExpiredContact | null
  shareToken: string | null
}) {
  // idle → sending → sent | error. `sent` is terminal so a client can't spam.
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')

  const sendRequest = () => {
    if (!shareToken || state === 'sending' || state === 'sent') return
    setState('sending')
    requestRefresh(shareToken)
      .then(() => setState('sent'))
      .catch(() => setState('error'))
  }

  return (
    <div
      data-testid="expired-contact-card"
      className="w-full max-w-xs rounded-2xl bg-[var(--surface-secondary)] border border-[var(--border-default)] p-4 flex flex-col gap-3"
    >
      <div className="text-[13px] text-[var(--text-secondary)]">
        Photographed by <span className="font-semibold text-[var(--text-primary)]">{name}</span>
      </div>
      {contact?.email && (
        <a
          href={`mailto:${contact.email}`}
          className="inline-flex items-center justify-center gap-2 text-[13px] text-[var(--text-primary)] hover:underline"
        >
          <Mail size={15} strokeWidth={2} />
          {contact.email}
        </a>
      )}

      {state === 'sent' ? (
        <div
          data-testid="refresh-request-sent"
          className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl bg-[var(--accent-success-soft,var(--surface-tertiary))] text-sm text-[var(--text-primary)]"
        >
          <Check size={16} strokeWidth={2.5} />
          Request sent
        </div>
      ) : (
        <button
          data-testid="request-refresh-button"
          onClick={sendRequest}
          disabled={state === 'sending' || !shareToken}
          className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl bg-[var(--text-primary)] text-[var(--surface-primary)] text-sm font-medium disabled:opacity-60"
        >
          {state === 'sending' && <Loader2 size={15} className="animate-spin" />}
          {state === 'error' ? 'Try again' : 'Request a refresh'}
        </button>
      )}
    </div>
  )
}

function NoLinkScreen() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[100dvh] gap-4 px-6 text-center">
      <div className="w-14 h-14 rounded-full bg-[var(--surface-secondary)] border border-[var(--border-default)] grid place-items-center text-[var(--text-secondary)]">
        <Camera size={26} strokeWidth={2} />
      </div>
      <div className="max-w-xs">
        <div className="text-base font-semibold text-[var(--text-primary)] mb-1">
          ShotSelect Picks
        </div>
        <div className="text-[13px] text-[var(--text-secondary)]">
          Open the link your photographer sent you. Each link includes a token that loads your
          private gallery.
        </div>
      </div>
    </div>
  )
}
